import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SearchIcon from '@mui/icons-material/Search';

import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';

import Grid from '@mui/material/Grid';
import React, { useEffect, useMemo, useState } from 'react';

import {
    createShuntingJob,
    getShuntingJobs,
    updateShuntingJob,
    updateShuntingJobStatus
} from '../../api/shuntingApi';

import { useSelector } from 'react-redux';
import AppDialogMessage from '../../components/Common/AppDialogMessage';
import ShuntingInvoice from '../../components/Invoice/ShuntingInvoice';
import NavBar from '../../components/NavBar/NavBar';
import { useAppDialog } from '../../Hooks/useAppDialog';

const emptyJob = {
    company_name: 'G2000',
    location: '',
    job_type: '',
    trailer_number: '',
    notes: '',
    job_date: '',
    start_time: '',
    end_time: '',
    duration_minutes: 0,
    billing_type: '',
    rate: 60,
    quantity: 0,
    amount: 0,
    tax: 0,
    total_amount: 0,
    invoice_number: '',
    invoiced: false,
    paid: false,
};

const ShuntingJobs: React.FC = () => {
    const [jobs, setJobs] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const [search, setSearch] = useState('');
    const [jobType, setJobType] = useState('');
    const [location, setLocation] = useState('');

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [totalRows, setTotalRows] = useState(0);

    const [editModal, setEditModal] = useState(false);
    const [job, setJob] = useState<any>(emptyJob);
    const [createModal, setCreateModal] = useState(false);

    const { dialog, openDialog, closeDialog } = useAppDialog();

    const user = useSelector((state: any) => state.user.user);
    const isAccountant = useMemo(() =>
        user.scope === 'Admin' && user.category === 'CLIENT'
        , [user]);

    const isSuperAdmin = useMemo(() =>
        user.scope === 'SuperAdmin'
        , [user]);
    const isVendor = useMemo(() =>
        user.category === 'VENDOR'
        , [user]);
    const fetchJobs = async () => {
        try {
            setLoading(true);

            const response = await getShuntingJobs({
                page: page + 1,
                limit: rowsPerPage,
                search,
                job_type: jobType,
                location,
                approved: isAccountant ? true : undefined
            });

            setJobs(response?.data || []);
            setTotalRows(response?.pagination?.total || 0);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchJobs();
    }, [page, rowsPerPage]);

    const handleSearch = () => {
        setPage(0);
        fetchJobs();
    };

    const handleEdit = (selectedJob: any) => {
        setJob(selectedJob);
        setEditModal(true);
    };

    const handleSave = async () => {
        try {
            const result = await updateShuntingJob(job);
            if (result?.data) {
                setJobs((prev) =>
                    prev.map((item) =>
                        item.id === job.id ? { ...result.data } : item
                    )
                );
            }

            setEditModal(false);

            openDialog({
                variant: 'success',
                title: 'Job Updated',
                message: result?.message || 'Job updated successfully.',
                confirmText: 'Okay',
                hideCancel: true,
            });

        } catch (error: any) {
            console.log(error);

            openDialog({
                variant: 'error',
                title: 'Update Failed',
                message: error?.message || 'Unable to update job.',
                confirmText: 'Close',
                hideCancel: true,
            });
        }
    };

    const handleApprovalToggle = async (
        item: any,
        field: 'approved' | 'paid'
    ) => {

        try {

            const result =
                await updateShuntingJobStatus({
                    id: item.id,
                    field,
                    value: !item[field],
                });

            const updatedJob =
                result?.data;

            if (!updatedJob) {

                return openDialog({
                    variant: 'error',
                    title: 'Update Failed',
                    message:
                        result?.data?.message ||
                        'Unable to update status.',
                    confirmText: 'Close',
                    hideCancel: true,
                });
            }

            openDialog({
                variant: 'success',
                title: !item[field]
                    ? 'Status Updated'
                    : 'Status Reverted',
                message:
                    result?.data?.message ||
                    'Status updated successfully.',
                confirmText: 'Okay',
                hideCancel: true,
            });

            setJobs((prev: any[]) =>
                prev.map((job) =>
                    job.id === item.id
                        ? updatedJob
                        : job
                )
            );

        } catch (error) {

            console.log(
                'Status update error:',
                error
            );
        }
    };

    const handleCreate = async () => {
        try {

            const result = await createShuntingJob(job);

            setJobs((prev) => [
                result.data,
                ...prev,
            ]);

            setCreateModal(false);
            setJob(emptyJob);

            openDialog({
                variant: 'success',
                title: 'Job Created',
                message: result?.message || 'Job created successfully.',
                confirmText: 'Okay',
                hideCancel: true,
            });

        } catch (error: any) {
            console.log(error);

            openDialog({
                variant: 'error',
                title: 'Create Failed',
                message: error?.message || 'Unable to create job.',
                confirmText: 'Close',
                hideCancel: true,
            });
        }
    };

    const handleTimeChange = (field: 'start_time' | 'end_time', value: string) => {

        const startTime = field === 'start_time' ? new Date(value) : job.start_time ? new Date(job.start_time) : null;
        const endTime = field === 'end_time' ? new Date(value) : job.end_time ? new Date(job.end_time) : null;
        if (startTime && endTime) {
            // Calculate duration in minutes
            const duration = (endTime.getTime() - startTime.getTime()) / (1000 * 60);
            setJob({
                ...job,
                [field]: value,
                duration_minutes: duration,
            });
        }
    };
    const renderJobForm = (isCreate = false) => (
        <Stack spacing={3} sx={{ mt: 1 }}>

            {/* ===================== */}
            {/* JOB INFORMATION */}
            {/* ===================== */}

            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    borderRadius: 2,
                }}
            >

                <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ mb: 2 }}
                >
                    Job Information
                </Typography>

                <Grid container spacing={2}>

                    {/* COMPANY */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            required
                            label="Company Name"
                            value={job.company_name || ''}
                            onChange={(e) =>
                                setJob({
                                    ...job,
                                    company_name: e.target.value,
                                })
                            }
                        />
                    </Grid>

                    {/* LOCATION */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            label="Location"
                            value={job.location || ''}
                            onChange={(e) =>
                                setJob({
                                    ...job,
                                    location: e.target.value,
                                })
                            }
                        />
                    </Grid>
                    {/* DRIVER NAME */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            label="Driver Name"
                            value={job.driver_name || ''}
                            onChange={(e) =>
                                setJob({
                                    ...job,
                                    driver_name: e.target.value,
                                })
                            }
                        />
                    </Grid>

                    {/* JOB TYPE */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            select
                            fullWidth
                            label="Job Type"
                            value={job.job_type || ''}
                            onChange={(e) =>
                                setJob({
                                    ...job,
                                    job_type: e.target.value,
                                })
                            }
                        >
                            <MenuItem value="SHUNT">
                                SHUNT
                            </MenuItem>

                            <MenuItem value="TRAILER_SWAP">
                                TRAILER SWAP
                            </MenuItem>

                            <MenuItem value="OTHER">
                                OTHER
                            </MenuItem>
                        </TextField>
                    </Grid>

                    {/* JOB DATE */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Job Date"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            value={
                                job.job_date
                                    ? job.job_date.split('T')[0]
                                    : ''
                            }
                            onChange={(e) =>
                                setJob({
                                    ...job,
                                    job_date: e.target.value,
                                })
                            }
                        />
                    </Grid>

                    {/* TRAILER */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            label="Trailer Number"
                            value={job.trailer_number || ''}
                            onChange={(e) =>
                                setJob({
                                    ...job,
                                    trailer_number: e.target.value,
                                })
                            }
                        />
                    </Grid>

                </Grid>

            </Paper>

            {/* ===================== */}
            {/* TIME INFORMATION */}
            {/* ===================== */}

            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    borderRadius: 2,
                }}
            >

                <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ mb: 2 }}
                >
                    Time Information
                </Typography>

                <Grid container spacing={2}>

                    {/* START TIME */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            type="datetime-local"
                            label="Start Time"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            value={job.start_time || ''}
                            onChange={(e) => {
                                setJob({
                                    ...job,
                                    start_time: e.target.value,
                                })
                                handleTimeChange('start_time', e.target.value)
                            }
                            }
                        />
                    </Grid>

                    {/* END TIME */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            type="datetime-local"
                            label="End Time"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            value={job.end_time || ''}
                            onChange={(e) => {
                                setJob({
                                    ...job,
                                    end_time: e.target.value,
                                })
                                handleTimeChange('end_time', e.target.value)
                            }
                            }
                        />
                    </Grid>

                    {/* DURATION */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Duration (Minutes)"
                            value={job.duration_minutes || 0}
                            onChange={(e) =>
                                setJob({
                                    ...job,
                                    duration_minutes: Number(e.target.value),
                                })
                            }
                        />
                    </Grid>

                </Grid>

            </Paper>

            {/* ===================== */}
            {/* BILLING INFORMATION */}
            {/* ===================== */}

            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    borderRadius: 2,
                }}
            >

                <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ mb: 2 }}
                >
                    Billing Information
                </Typography>

                <Grid container spacing={2}>

                    {/* RATE */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Rate Per Hour"
                            value={job.rate || 0}
                            onChange={(e) =>
                                setJob({
                                    ...job,
                                    rate: Number(e.target.value),
                                })
                            }
                        />
                    </Grid>

                    {/* TOTAL */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            fullWidth
                            disabled
                            label="Total Amount"
                            value={(
                                ((job.duration_minutes || 0) / 60) *
                                (job.rate || 0)
                            ).toFixed(2)}
                        />
                    </Grid>

                    {/* INVOICE */}
                    <Grid size={{ xs: 12, md: 4 }}>

                        <TextField
                            select
                            fullWidth
                            label="Paid"
                            disabled={!isSuperAdmin}
                            value={job.paid ? 'YES' : 'NO'}
                            onChange={(e) =>
                                setJob({
                                    ...job,
                                    paid: e.target.value === 'YES',
                                })
                            }
                        >
                            <MenuItem value="YES">
                                YES
                            </MenuItem>

                            <MenuItem value="NO">
                                NO
                            </MenuItem>
                        </TextField>
                    </Grid>

                    {/* PAID */}
                    <Grid size={{ xs: 12, md: 6 }}>
                    </Grid>

                </Grid>

            </Paper>

            {/* ===================== */}
            {/* NOTES */}
            {/* ===================== */}

            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    borderRadius: 2,
                }}
            >

                <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ mb: 2 }}
                >
                    Notes
                </Typography>

                <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    label="Notes"
                    value={job.notes || ''}
                    onChange={(e) =>
                        setJob({
                            ...job,
                            notes: e.target.value,
                        })
                    }
                />

            </Paper>

            {/* ===================== */}
            {/* ACTIONS */}
            {/* ===================== */}

            <DialogActions sx={{ px: 0 }}>

                <Button
                    variant="outlined"
                    onClick={() => {

                        if (isCreate) {
                            setCreateModal(false);
                        } else {
                            setEditModal(false);
                        }

                        setJob(emptyJob);
                    }}
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    onClick={
                        isCreate
                            ? handleCreate
                            : handleSave
                    }
                >
                    {isCreate ? 'Create' : 'Save'}
                </Button>

            </DialogActions>

        </Stack>
    );
    return (
        <>
            <NavBar />
            {/* CREATE MODAL */}
            <Dialog
                open={createModal}
                onClose={() => {
                    setCreateModal(false);
                    setJob(emptyJob);
                }}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>
                    Create Shunting Job
                </DialogTitle>

                <DialogContent dividers>
                    {renderJobForm(true)}
                </DialogContent>
            </Dialog>
            {/* EDIT MODAL */}
            <Dialog
                open={editModal}
                onClose={() => {
                    setEditModal(false);
                    setJob(emptyJob);
                }}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>
                    Edit Shunting Job <span style={{ fontWeight: 800, color: 'grey' }}>#{job.invoice_no || ''}</span>
                </DialogTitle>

                <DialogContent dividers>
                    {renderJobForm(false)}
                </DialogContent>
            </Dialog>

            <Box>

                {/* HEADER */}
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                >
                    <Typography variant="h6" fontWeight={800}>
                        Trailer Shunting Jobs
                    </Typography>

                    {!isAccountant && (<Button
                        variant="contained"
                        size='small'
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setJob(emptyJob);
                            setCreateModal(true);
                        }}
                    >
                        Add Job
                    </Button>)}
                </Stack>

                {/* FILTERS */}
                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        mb: 2,
                    }}
                >
                    <Grid container spacing={2}>

                        <Grid size={{ xs: 12, md: 7 }}>
                            <TextField
                                fullWidth
                                placeholder="Search jobs..."
                                value={search}
                                onChange={(e) =>
                                    setSearch(e.target.value)
                                }
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        {/* <Grid size={{ xs: 12, md: 3 }}>
                            <TextField
                                select
                                fullWidth
                                label="Job Type"
                                value={jobType}
                                onChange={(e) =>
                                    setJobType(e.target.value)
                                }
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="SHUNT">SHUNT</MenuItem>
                                <MenuItem value="DOCK_MOVE">DOCK MOVE</MenuItem>
                                <MenuItem value="YARD_MOVE">YARD MOVE</MenuItem>
                                <MenuItem value="TRAILER_SWAP">TRAILER SWAP</MenuItem>
                            </TextField>
                        </Grid> */}
                        <Grid size={{ xs: 12, md: 3 }}>
                            <TextField
                                fullWidth
                                label="Location"
                                value={location}
                                onChange={(e) =>
                                    setLocation(e.target.value)
                                }
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 2 }}>
                            <Button
                                fullWidth
                                variant="contained"
                                sx={{ height: '100%' }}
                                onClick={handleSearch}
                            >
                                Search
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>

                {/* TABLE */}
                <Paper
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                    }}
                >
                    <TableContainer
                        sx={{
                            maxHeight: 'calc(100dvh - 340px)',
                        }}
                    >
                        <Table
                            stickyHeader
                            size="small"
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            fontWeight: 700,
                                            minWidth: 110,
                                        }}
                                    >
                                        Invoice No.
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 700,
                                            minWidth: 180,
                                        }}
                                    >
                                        Company
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 700,
                                            minWidth: 160,
                                        }}
                                    >
                                        Location
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 700,
                                            minWidth: 130,
                                        }}
                                    >
                                        Job Type
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 700,
                                            minWidth: 130,
                                        }}
                                    >
                                        Trailer
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 700,
                                            minWidth: 120,
                                        }}
                                    >
                                        Job Date
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{
                                            fontWeight: 700,
                                            minWidth: 120,
                                        }}
                                    >
                                        Duration
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{
                                            fontWeight: 700,
                                            minWidth: 120,
                                        }}
                                    >
                                        Rate
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{
                                            fontWeight: 700,
                                            minWidth: 140,
                                        }}
                                    >
                                        Total Amount (With HST)
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            fontWeight: 700,
                                            minWidth: 110,
                                        }}
                                    >
                                        {isAccountant ? 'Approval' : 'Payment'}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>
                                        {isAccountant ? 'Payment' : 'Approval'}
                                    </TableCell>
                                    {!isAccountant && !isVendor && <TableCell
                                        align="center"
                                        sx={{
                                            width: 70,
                                            fontWeight: 700,
                                        }}
                                    >
                                        Action
                                    </TableCell>}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {!loading && jobs.length > 0 ? (
                                    jobs.map((item: any) => (
                                        <TableRow
                                            key={item.id}
                                            hover
                                        >

                                            {/* INVOICE */}
                                            <TableCell
                                                sx={{
                                                    fontWeight: 600,
                                                }}
                                            >
                                                <ShuntingInvoice
                                                    job={item}
                                                />
                                                {/* {item.invoice_no} */}
                                            </TableCell>

                                            {/* COMPANY */}
                                            <TableCell>
                                                {item.company_name}
                                            </TableCell>

                                            {/* LOCATION */}
                                            <TableCell>
                                                {item.location || '-'}
                                            </TableCell>

                                            {/* JOB TYPE */}
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={item.job_type || '-'}
                                                    variant="outlined"
                                                />
                                            </TableCell>

                                            {/* TRAILER */}
                                            <TableCell>
                                                {item.trailer_number || '-'}
                                            </TableCell>

                                            {/* DATE */}
                                            <TableCell>
                                                {item.job_date
                                                    ? new Date(item.job_date)
                                                        .toLocaleDateString()
                                                    : '-'}
                                            </TableCell>

                                            {/* DURATION */}
                                            <TableCell align="right">
                                                {item.duration_minutes || 0} mins
                                            </TableCell>

                                            {/* RATE */}
                                            <TableCell align="right">
                                                $
                                                {Number(item.rate || 0)
                                                    .toFixed(2)}
                                            </TableCell>

                                            {/* TOTAL */}
                                            <TableCell
                                                align="right"
                                                sx={{
                                                    fontWeight: 700,
                                                }}
                                            >
                                                $
                                                {Number(
                                                    item.total_amount * 1.13 || 0
                                                ).toFixed(2)}
                                            </TableCell>

                                            {/* PAID */}
                                            <TableCell align="center">
                                                {isAccountant ? item?.approved ?
                                                    <Chip
                                                        size="small"
                                                        label="Approved"
                                                        color="success"
                                                    />
                                                    :
                                                    <Chip
                                                        size="small"
                                                        label="Pending"
                                                        color="warning"
                                                    /> :
                                                    item.paid ? (
                                                        <Chip
                                                            size="small"
                                                            label="Paid"
                                                            color="success"
                                                        />
                                                    ) : (
                                                        <Chip
                                                            size="small"
                                                            label="Pending"
                                                            color="warning"
                                                        />
                                                    )}
                                            </TableCell>
                                            <TableCell align="center">
                                                {/* ACTION BUTTON */}
                                                {isAccountant ? (<Button
                                                    size="small"
                                                    variant={
                                                        item.paid
                                                            ? 'outlined'
                                                            : 'contained'
                                                    }
                                                    color={
                                                        item.paid
                                                            ? 'warning'
                                                            : 'success'
                                                    }
                                                    onClick={() =>
                                                        handleApprovalToggle(item, 'paid')
                                                    }
                                                >
                                                    {item.paid
                                                        ? 'Revert'
                                                        : 'Pay'}
                                                </Button>) : isVendor ? item?.approved ?
                                                    <Chip
                                                        size="small"
                                                        label="Approved"
                                                        color="success"
                                                    />
                                                    :
                                                    <Chip
                                                        size="small"
                                                        label="Pending"
                                                        color="warning"
                                                    /> : (<Button
                                                        size="small"
                                                        variant={
                                                            item.approved
                                                                ? 'outlined'
                                                                : 'contained'
                                                        }
                                                        color={
                                                            item.approved
                                                                ? 'warning'
                                                                : 'success'
                                                        }
                                                        disabled={item.paid}
                                                        onClick={() =>
                                                            handleApprovalToggle(item, 'approved')
                                                        }
                                                    >
                                                        {item.approved
                                                            ? 'Revert'
                                                            : 'Approve'}
                                                    </Button>)}

                                            </TableCell>

                                            {/* ACTION */}
                                            {!isAccountant && !isVendor && (<TableCell align="center">
                                                <IconButton
                                                    size="small"
                                                    disabled={item.paid}
                                                    onClick={() => handleEdit(item)}
                                                >
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>)}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell
                                            colSpan={11}
                                            align="center"
                                            sx={{
                                                py: 4,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {loading
                                                    ? 'Loading jobs...'
                                                    : 'No shunting jobs found.'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={totalRows}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={(_, newPage) =>
                            setPage(newPage)
                        }
                        onRowsPerPageChange={(e) => {

                            setRowsPerPage(
                                parseInt(
                                    e.target.value,
                                    10
                                )
                            );

                            setPage(0);
                        }}
                        rowsPerPageOptions={[
                            10,
                            20,
                            50,
                            100,
                        ]}
                    />

                </Paper>
            </Box>

            <AppDialogMessage
                open={dialog.open}
                title={dialog.title}
                message={dialog.message}
                variant={dialog.variant}
                confirmText={dialog.confirmText}
                cancelText={dialog.cancelText}
                hideCancel={dialog.hideCancel}
                onConfirm={dialog.onConfirm}
                onClose={closeDialog}
            />
        </>
    );
};

export default ShuntingJobs;